/**
 * El panel que se abre debajo de una fila del historial de ventas.
 *
 * Es donde vive todo lo que la fila dejo de mostrar. La fila quedo en una sola
 * linea —fecha, numero, cliente, estado, importe— y eso solo se pudo hacer
 * porque lo secundario tiene ahora un lugar con aire: la cotizacion congelada,
 * el equivalente en pesos, las lineas vendidas y como se cobro.
 *
 * COMO SE COBRO SON DOS LISTAS Y NINGUNA ALCANZA SOLA
 * ---------------------------------------------------
 * `pagos` es la plata (`movimientos_caja`). `entregas` es el equipo que el
 * cliente dio en parte de pago, que NO toca caja: `emitir_venta_contado` lo da
 * de alta como producto, lo ingresa al stock y suma su valor de toma a lo
 * cobrado sin insertar un solo movimiento.
 *
 * Por eso una venta canjeada al 100% por un celular tiene `pagos` vacio y esta
 * cobrada igual. Mostrar solo `pagos` la dibujaria como cobrada y sin ningun
 * pago, que es exactamente la clase de mentira que esta pantalla ya conto una
 * vez (ver el encabezado de `routes/ventas.tsx`).
 *
 * UN SOLO NUMERO POR FILA, Y EL EQUIVALENTE AL PASAR EL MOUSE
 * -----------------------------------------------------------
 * Una venta en dolares se cobra casi siempre en pesos, y a veces en las dos.
 * Hubo una version que ponia al lado de cada pago su equivalente en la moneda
 * de la venta, para que la columna sumara el total a la vista. Se probo con
 * datos reales y confundia mas de lo que explicaba: dos cifras por fila, dos
 * chips de moneda, y la fila dejaba de leerse de un vistazo.
 *
 * Queda una sola cifra: la del medio con el que se pago, en su moneda. El
 * equivalente vive en un tooltip, para quien lo necesite y sin costo visual
 * para quien no.
 *
 * Lo que eso cuesta, dicho de frente: el "Cobrado" del pie ya no se puede
 * verificar sumando las filas. No es una suma que calcule esta pantalla —es
 * `comprobantes.pagado`, un dato guardado— pero el lector tampoco puede
 * comprobarlo, y eso es una eleccion consciente, no un descuido.
 */
import { useMemo } from "react";
import { Package } from "lucide-react";

import { convertir, formatearCotizacion, type Moneda } from "@tora/shared";
import { mediosDe, etiquetaMedio, type CuentaFinanciera } from "@/components/cobro/logica";
import { Importe } from "@/components/money/importe";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useVenta } from "@/lib/queries";
import {
  aNumero,
  type Comprobante,
  type EntregaDeVenta,
  type MovimientoCaja,
  type Numerico,
} from "@/lib/tipos";
import { cn } from "@/lib/utils";

export interface DetalleVentaProps {
  comprobante: Comprobante;
  /** Para ponerle nombre a la caja de cada pago: el movimiento trae `cuenta_id`. */
  cuentas: CuentaFinanciera[];
}

/** Una linea de "como se cobro", ya normalizada: la plata y el canje se dibujan igual. */
interface LineaCobro {
  clave: string;
  titulo: string;
  /** Debajo del titulo: el medio cuando desambigua, o el equipo entregado. */
  detalle?: string;
  imei?: string;
  esCanje: boolean;
  /** En la moneda con la que se pago de verdad. Nunca convertido. */
  importe: number;
  moneda: Moneda;
}

export function DetalleVenta({ comprobante: c, cuentas }: DetalleVentaProps): JSX.Element {
  const { data, isLoading, isError } = useVenta(c.id);

  const cuentasPorId = useMemo(() => new Map(cuentas.map((cu) => [cu.id, cu])), [cuentas]);

  if (isLoading) {
    return (
      <div className="p-4">
        <Cargando texto={`Abriendo ${c.numero}…`} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="p-4 text-sm text-destructive">
        No se pudo abrir el detalle de {c.numero}. Probá de nuevo.
      </p>
    );
  }

  const { detalle, pagos, entregas } = data;
  const cotizacion = aNumero(c.cotizacion);

  return (
    /*
      `divide-y` en vez de separacion al aire: una regla entre cada concepto.
      Con los bloques solo espaciados, "Qué se vendió" y "Cómo se cobró" se leian
      como una sola lista larga y el lector no sabia donde terminaba uno.
    */
    <div className="divide-y divide-border px-4 [&>*]:py-4">
      <section className="space-y-2">
        <TituloSeccion nivel="etiqueta">Qué se vendió</TituloSeccion>
        {detalle.length === 0 ? (
          <p className="text-sm text-muted-foreground">Este comprobante no tiene líneas.</p>
        ) : (
          <ul className="divide-y divide-border">
            {detalle.map((l) => (
              <li key={l.id} className="flex items-baseline gap-3 py-1.5">
                <span className="min-w-0 flex-1">
                  <span className="text-sm">
                    <span className="tabular-nums text-muted-foreground">
                      {aNumero(l.cantidad)}×{" "}
                    </span>
                    {l.descripcion}
                  </span>
                  {/* El IMEI es la respuesta a "que aparato le vendi a este". */}
                  {l.serie_imei ? (
                    <Codigo className="block text-xs text-muted-foreground">{l.serie_imei}</Codigo>
                  ) : null}
                </span>
                <span className="shrink-0">
                  <Importe monto={aNumero(l.importe)} moneda={c.moneda} tamano="sm" />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ComoSeCobro
        comprobante={c}
        pagos={pagos}
        entregas={entregas}
        cuentasPorId={cuentasPorId}
        cotizacion={cotizacion}
      />

      {/*
        La cotizacion va al pie y abarca todo, porque es de la VENTA y no de una
        de las dos secciones: es la que convierte cada pago y la que dice cuanto
        valia esto en pesos. Solo en las ventas en dolares: en una venta en pesos
        no convierte nada y seria ruido.
      */}
      {c.moneda === "USD" ? (
        <p className="text-xs text-muted-foreground">
          Dólar a {formatearCotizacion(cotizacion)} el día de la venta · la venta equivale a{" "}
          <Importe monto={aNumero(c.total_ars)} moneda="ARS" tamano="sm" />
        </p>
      ) : null}
    </div>
  );
}

/**
 * El nombre de un pago.
 *
 * Se muestra la CAJA y no el medio: la caja se llama "Efectivo" y el medio
 * tambien, asi que "{medio} · {caja}" dibujaba "Efectivo · Efectivo".
 *
 * El medio vuelve como detalle solo cuando desambigua, y eso lo decide el mismo
 * `mediosDe` que usa el editor de cobro: una caja acepta un solo medio (el
 * efectivo de una caja de pesos solo puede ser efectivo en pesos), asi que ahi
 * el medio es puro ruido; un banco acepta varios, y ahi saber si entro por
 * transferencia o con cheque si dice algo.
 */
export function nombreDelPago(
  p: MovimientoCaja,
  cuenta: CuentaFinanciera | undefined,
): { titulo: string; detalle?: string } {
  if (!cuenta) {
    return { titulo: p.medio ? etiquetaMedio(p.medio) : "Cobro", detalle: `Caja #${p.cuenta_id}` };
  }
  const ambiguo = mediosDe(cuenta).length > 1;
  return {
    titulo: cuenta.nombre,
    detalle: ambiguo && p.medio ? etiquetaMedio(p.medio) : undefined,
  };
}

/**
 * El importe de un pago: UNA cifra, en la moneda con la que se pago.
 *
 * Cuando esa moneda no es la de la venta, el equivalente aparece al pasar el
 * mouse. Va en un tooltip y no en la fila porque dos cifras por linea hacian
 * que la fila dejara de leerse de un vistazo: el equivalente lo necesita quien
 * esta cuadrando la venta, no quien la esta mirando de paso.
 *
 * `Tooltip` de Radix responde tambien al foco, asi que se llega con Tab.
 */
function ImporteDelPago({
  linea: l,
  monedaVenta,
  cotizacion,
}: {
  linea: LineaCobro;
  monedaVenta: Moneda;
  cotizacion: number;
}): JSX.Element {
  const importe = <Importe monto={l.importe} moneda={l.moneda} tamano="sm" />;

  if (l.moneda === monedaVenta) return <span className="shrink-0">{importe}</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-help decoration-dotted underline-offset-4 hover:underline">
          {importe}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Equivale a{" "}
        <Importe
          monto={convertir(l.importe, l.moneda, monedaVenta, cotizacion)}
          moneda={monedaVenta}
          tamano="sm"
        />{" "}
        a la cotización de esta venta
      </TooltipContent>
    </Tooltip>
  );
}

function ComoSeCobro({
  comprobante: c,
  pagos,
  entregas,
  cuentasPorId,
  cotizacion,
}: {
  comprobante: Comprobante;
  pagos: MovimientoCaja[];
  entregas: EntregaDeVenta[];
  cuentasPorId: ReadonlyMap<number, CuentaFinanciera>;
  cotizacion: number;
}): JSX.Element {
  const total = aNumero(c.total);
  // `pagado` lo trae `GET /ventas` pero el tipo compartido todavia no lo
  // declara; mismo tipo local que usa `estadosDe` en la lista.
  const pagado = aNumero((c as Comprobante & { pagado?: Numerico | null }).pagado ?? 0);
  const falta = total - pagado;

  const lineas: LineaCobro[] = [
    ...pagos.map((p): LineaCobro => {
      const { titulo, detalle } = nombreDelPago(p, cuentasPorId.get(p.cuenta_id));
      // Una devolucion es un EGRESO: sale plata, va en negativo.
      const importe = (p.tipo === "EGRESO" ? -1 : 1) * aNumero(p.importe);
      return {
        clave: `pago-${p.id}`,
        titulo,
        detalle,
        esCanje: false,
        importe,
        moneda: p.moneda,
      };
    }),
    ...entregas.map((e): LineaCobro => {
      const moneda = e.costo_moneda ?? c.moneda;
      const importe = aNumero(e.costo_unitario ?? 0);
      return {
        clave: `entrega-${e.id}`,
        titulo: "Parte de pago",
        detalle: e.producto_nombre,
        imei: e.producto_imei ?? undefined,
        esCanje: true,
        importe,
        moneda,
      };
    }),
  ];

  return (
    <section className="space-y-2">
      <TituloSeccion nivel="etiqueta">Cómo se cobró</TituloSeccion>

      {lineas.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {pagado > 0.009
            ? // Esta cobrado y no hay ni un pago ni una entrega: lo unico que
              // queda es un cobro imputado despues, desde la cuenta corriente.
              "Se imputó desde la cuenta corriente del cliente."
            : "Todavía no se cobró nada."}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-border">
            {lineas.map((l) => (
              <li key={l.clave} className="flex items-baseline gap-3 py-1.5 text-sm">
                <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                  {l.esCanje ? (
                    <Package
                      className="size-3.5 shrink-0 translate-y-0.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className="min-w-0">
                    {l.titulo}
                    {l.detalle ? (
                      <span className="text-muted-foreground"> · {l.detalle}</span>
                    ) : null}
                    {l.imei ? (
                      <Codigo className="block text-xs text-muted-foreground">{l.imei}</Codigo>
                    ) : null}
                  </span>
                </span>

                <ImporteDelPago linea={l} monedaVenta={c.moneda} cotizacion={cotizacion} />
              </li>
            ))}
          </ul>

          <div
            className={cn(
              "mt-1 flex items-baseline gap-3 border-t-2 border-border pt-2 text-sm font-medium",
              falta > 0.009 && "text-destructive",
            )}
          >
            <span className="flex-1">{falta > 0.009 ? "Falta cobrar" : "Cobrado"}</span>
            <span className="shrink-0">
              <Importe monto={falta > 0.009 ? falta : pagado} moneda={c.moneda} tamano="sm" />
            </span>
          </div>
        </>
      )}
    </section>
  );
}
